import { useCallback } from "react";

import { useMsal } from "@azure/msal-react";
import { LoginRequest } from "configs";
import { alert, message } from "contexts";
import { useAuth } from "hooks";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { authLoginService } from "services";
import i18n from "translations/i18n";
import { saveAccessToken, saveRefreshToken } from "utils";

export const useSignIn = () => {
  const { initUserSession } = useAuth();
  const { mutate: login, isLoading } = useMutation({
    mutationFn: authLoginService,
    mutationKey: "login",
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => {
      message({
        title: i18n.t("loginFailed"),
        content:
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          (Object.values(e?.response?.data?.message || {})?.[0] as string) ||
          "",
        type: "Error",
        buttonLabel: i18n.t("tryAgain"),
      });
    },
    onSuccess: (res) => {
      if (res?.data) {
        const { accessToken = "", refreshToken = "" } = res.data;
        saveAccessToken(accessToken);
        saveRefreshToken(refreshToken ?? refreshToken);
        initUserSession();
      }
    },
  });

  const { instance } = useMsal();
  const { t } = useTranslation();

  const handleLoginViaAzure = async () => {
    try {
      const azureRes = await instance.loginPopup(LoginRequest);
      if (azureRes) {
        login({
          provider: "azure",
          authData: {
            accessToken: azureRes?.accessToken,
            idToken: azureRes?.idToken,
            oid: (azureRes?.idTokenClaims as { oid: string })?.oid,
          },
        });
      }
    } catch {
      alert({
        title: t("loginFailed"),
        content: t("pleaseEnsureYourCredentialsAreValidAndTryAgain"),
      });
    }
  };

  const handleLoginPassword = useCallback(
    (email: string, password: string) => {
      login({
        provider: "password",
        authData: {
          email,
          password,
        },
      });
    },
    [login]
  );

  const handleLoginWithOTP = useCallback(
    (email: string, otp: string, csrfToken: string) => {
      login({
        provider: "otp",
        authData: {
          email,
          code: otp,
          csrfToken,
        },
      });
    },
    [login]
  );

  return {
    handleLoginViaAzure,
    handleLoginPassword,
    handleLoginWithOTP,
    isLoading,
  };
};
